'use client';

/** Pill-shaped input variant with an animated floating label. */

import * as React from 'react';
import {
	getFormFieldFocusRingStyles,
	buildFormFieldWrapperStyles,
	buildFormFieldHelperTextStyles,
} from '../primitives/hooks/useFormFieldStyles.ts';
import { buildFloatingContainerStyles, buildFloatingInputStyles, buildFloatingLabelStyles } from '../primitives/Input.styles.ts';
import { BARE_BOX_OVERRIDES } from '../primitives/input-helpers.ts';
import { FieldLabel, ContentInputCore } from '../primitives/input-components.tsx';
import { ContentFieldFooter } from './FieldFooter.tsx';
import type { InputContentBaseProps } from '@federa/types/ui-input';

interface FloatingLabelProps {
	htmlFor: string;
	label: string;
	isFloating: boolean;
	isFocused: boolean;
	required?: boolean;
}

/** The floating variant is the SHARED `FieldLabel` under animated styles -- only the style builder differs, so the
 *  caption/required-indicator pair is not re-declared here. */
function FloatingLabel({ htmlFor, label, isFloating, isFocused, required }: FloatingLabelProps): React.ReactElement {
	return <FieldLabel htmlFor={htmlFor} style={buildFloatingLabelStyles(isFloating, isFocused)} label={label} required={required} />;
}

function useFloatingHasValue(value?: string | number | readonly string[], defaultValue?: string | number | readonly string[]) {
	const [hasValue, setHasValue] = React.useState(() => {
		const initialValue = value ?? defaultValue ?? '';
		return String(initialValue) !== '';
	});
	// Re-sync `hasValue` on controlled `value` change during render, not effect-then-setState. The `value !== undefined` guard preserves the uncontrolled case.
	const [prevValue, setPrevValue] = React.useState(value);
	if (value !== prevValue) {
		setPrevValue(value);
		if (value !== undefined) setHasValue(String(value) !== '');
	}
	return { hasValue, setHasValue };
}

// Bound to a NAMED parameter and destructured in the body for the reason its default-variant sibling records: the
// shared members travel to `ContentInputCore` / `ContentFieldFooter` as one object. `content`, not `props` -- the
// contract carries a member of its own by that name.
export function FloatingInputContent(content: InputContentBaseProps): React.ReactElement {
	const { inputId, label, state, stateStyle, isFocused, disabled, fullWidth, wrapperClassName, style, required, value, defaultValue, bare, props } =
		content;
	const { hasValue, setHasValue } = useFloatingHasValue(value, defaultValue);
	const isFloating = isFocused || hasValue;
	const containerStyles = buildFloatingContainerStyles(stateStyle, isFocused, disabled);
	const inputStyles = buildFloatingInputStyles(disabled);
	const focusRingStyles = getFormFieldFocusRingStyles(isFocused);

	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		setHasValue(e.target.value !== '');
		props.onChange?.(e);
	};

	return (
		<div style={buildFormFieldWrapperStyles(fullWidth)} className={wrapperClassName}>
			{/* `bare` lands on the CONTAINER here, not on the element: this variant draws its pill on the wrapper and its
			    input is already borderless and transparent, so the container IS the box the prop drops. Applying it to
			    the element instead would zero the asymmetric padding the floating label is positioned against, which is
			    why the two variants answer the same prop at different nodes rather than sharing one application point. */}
			<div style={{ ...containerStyles, ...(bare ? BARE_BOX_OVERRIDES : {}), ...focusRingStyles }}>
				{label && <FloatingLabel htmlFor={inputId} label={label} isFloating={isFloating} isFocused={isFocused} required={required} />}
				<ContentInputCore
					content={content}
					inputStyle={{ ...inputStyles, ...style }}
					// The `" "` is what drives the label animation: it makes `:placeholder-shown` false the moment the
					// field is empty-but-focused, which is the only per-variant argument this element still needs.
					placeholder=" "
					onChangeOverride={handleChange}
				/>
			</div>
			<ContentFieldFooter content={content} messageStyles={buildFormFieldHelperTextStyles(state)} />
		</div>
	);
}
